import classNames from 'classnames';
import {observer, Provider} from 'mobx-react';
import React, {useEffect, useRef} from 'react';

import {CourseProgressCardsSkeleton, BrowseCourse} from '@udemy/browse-course';
import {
    UI_REGION,
    UpdateCareerGoalSelected,
    GoalCollectionTargetPage,
    discoveryTracker,
    BackendSourceOptions,
} from '@udemy/browse-event-tracking';
import {GwgAccelerator} from '@udemy/career-academies';
import {DiscoveryUnit, DiscoveryUnitItem, PAGE_TYPE_LOGGED_IN_HOMEPAGE} from '@udemy/discovery-api';
import {Tracker, TrackingContextProvider} from '@udemy/event-tracking';
import {FunnelLogContextProvider} from '@udemy/funnel-tracking';
import {BadgeClass, FeatureVariantAssignment} from '@udemy/graphql';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {
    CertificationBundle,
    CertificationBundleUnit,
    CertificationPrepRecoBanner,
    getBundleFromTopic,
    type RecommendedCertificationVoucherExam,
} from '@udemy/react-certification-preparation';
import {
    CourseUnit,
    DiscoveryUnitsContainer,
    PopularTopicsUnit,
    SingleCourseUnit,
    SkillsHubCourseUnit,
    ValueProps,
    PopularTopicsDiscoveryItem,
} from '@udemy/react-discovery-units';
import {ExperimentImpressionEvent, useFeatures} from '@udemy/react-experimentation-provider';
import {
    CourseGuidanceEntryPointWithPreselection,
    EntryPointPopUp,
    TOP_10_COURSES_IN_COUNTRY_VIEW_TYPE,
    Top10CoursesComponent,
    TopCategoriesComponent,
} from '@udemy/react-globalization-components';
import {
    OnePaneLayoutContainer,
    OnePaneLayoutFullWidthSection,
} from '@udemy/react-layout-components';
import {LectureProgressTab, MyLearningUnitTabProvider} from '@udemy/react-my-learning-unit';
import {
    PERSONALIZE_URL,
    UserOccupationGoalHeader,
    UserOccupationInfo,
} from '@udemy/react-personalized-content';
import {TextSkeleton} from '@udemy/react-reveal-components';
import {StreaksBox, StreakUserLectureConsumptionWeeklyStore} from '@udemy/react-student-retention';
import {useUDData} from '@udemy/ud-data';

import {MyLearningUnitContainer} from 'src/components/my-learning-unit-container/my-learning-unit-container.react-component';
import {CertificationVoucherExam, useTopicInterestsQuery} from 'src/generated/graphql';
import {
    LOCALE_SPECIFIC_TOPICS,
    COURSE_GUIDANCE_FEATURE,
    USER_STREAKS_ON_MX_LIHP,
    CERTIFICATION_BUNDLE_UNIT_LIHP,
    LIHP_CERT_VOUCHER_RECO_BANNER,
    TOP_10_COURSES_JAPAN,
} from 'src/lib/experimentation-constants';
import {HomeContextData} from 'src/types/home-context';
import {UserEnrollmentsData} from 'src/types/page-props';
import {getCoursesData} from 'src/utils/get-courses-data';
import {personalizeDiscoveryUnitPropData} from 'src/utils/personalize-course-unit-props-helper';

import {
    CERT_BUNDLE_DISCOUNT_CODE,
    COURSE_RECOMMENDATIONS_SECTION_ID,
    FUNNEL_LOG_PAGE_TYPE,
    MY_COURSES_HOME_URL,
} from '../constants';
import {GwgSection} from './gwg-section.react-component';
import styles from './home-page.module.less';

interface HomePageFeatureProps {
    contextData: HomeContextData;
    userOccupationInfo: UserOccupationInfo;
    hasSeenUserGoalHeader: boolean;
    userEnrollments?: UserEnrollmentsData;
    accelerator?: GwgAccelerator;
    recommendedExam?: CertificationVoucherExam;
    topicSlug?: string;
    bundle?: CertificationBundle;
    certification?: BadgeClass;
}

export const HomePageFeature = observer((props: HomePageFeatureProps) => {
    const {gettext} = useI18n();
    const isMobileMax = useMatchMedia('mobile-max');
    const [streaksStore] = React.useState(() => new StreakUserLectureConsumptionWeeklyStore());
    const [showUserStreaks, setShowUserStreaks] = React.useState(false);
    const [certBundles, setCertBundles] = React.useState<CertificationBundle[]>([]);
    const udData = useUDData();
    const isPriceCountryJapan = udData.Config.price_country?.id === 'JP';

    const {featureVariantAssignments} = useFeatures([
        LOCALE_SPECIFIC_TOPICS,
        COURSE_GUIDANCE_FEATURE,
        USER_STREAKS_ON_MX_LIHP,
        CERTIFICATION_BUNDLE_UNIT_LIHP,
        LIHP_CERT_VOUCHER_RECO_BANNER,
        TOP_10_COURSES_JAPAN,
    ]);

    const localeSpecificTopicsFeature =
        featureVariantAssignments.get(LOCALE_SPECIFIC_TOPICS)?.configuration ?? {};
    const showCourseGuidanceFeature =
        featureVariantAssignments.get(COURSE_GUIDANCE_FEATURE)?.configuration ?? {};

    const userStreakOnMXLIHPExperiment = featureVariantAssignments.get(USER_STREAKS_ON_MX_LIHP);
    const certificationBundleUnitLIHPExperiment = featureVariantAssignments.get(
        CERTIFICATION_BUNDLE_UNIT_LIHP,
    );
    const lihpCertVoucherRecoBannerExperiment = featureVariantAssignments.get(
        LIHP_CERT_VOUCHER_RECO_BANNER,
    );
    const top10CoursesJapanExperiment = featureVariantAssignments.get(TOP_10_COURSES_JAPAN);

    const learningTab = useRef(
        new LectureProgressTab(
            0,
            'Courses',
            <CourseProgressCardsSkeleton />,
            gettext,
            undefined,
            true,
        ),
    );

    const triggerImpressionEvent = (feature: FeatureVariantAssignment | undefined) => {
        if (!feature) return;
        const {experimentIds, isLocalDefault} = feature;
        Tracker.publishEvent(new ExperimentImpressionEvent(experimentIds, isLocalDefault));
    };

    const getUnitComponentAndProps = (
        unit: Partial<DiscoveryUnit<DiscoveryUnitItem>> | undefined,
    ) => {
        if (!unit) throw new Error('Missing unit');

        const responsiveUnit = {
            fullWidth: isMobileMax,
            showPager: !isMobileMax,
            layout: isMobileMax ? 'multirow' : 'singlerow',
        };

        switch (unit.type) {
            case 'bestseller_labels':
                return {
                    component: PopularTopicsUnit,
                    contextObj: {
                        subcontext: 'affinity',
                    },
                    componentProps: {
                        trackImpression: (unit: DiscoveryUnit<PopularTopicsDiscoveryItem>) => {
                            discoveryTracker.trackUnitView(
                                {tracking_id: unit.tracking_id ?? '', title: unit.title ?? ''},
                                unit.item_type ?? '',
                            );
                        },
                        trackTopicClick: (topicId: string, topicName: string) => {
                            discoveryTracker.trackDiscoveryItemClickEvent(
                                {
                                    item: {
                                        id: Number(topicId),
                                        title: topicName,
                                        _class: 'course_topic',
                                        type: 'course_topic',
                                        tracking_id: topicId,
                                    },
                                },
                                {
                                    backendSource: BackendSourceOptions.DISCOVERY,
                                },
                            );
                        },
                    },
                };
            case 'top_pick':
            case 'featured_course':
                return {
                    component: SingleCourseUnit,
                    contextObject: {
                        context2: 'featured',
                        subcontext: unit.title,
                        subcontext2: unit.id,
                    },
                };
            case 'discovery_value_props':
                return {
                    component: ValueProps,
                };
            default:
                switch (unit.view_type) {
                    case 'horizontal_tabbed':
                        return {
                            component: SkillsHubCourseUnit,
                        };
                    case 'occupation':
                        return {
                            component: CourseUnit,
                            componentProps: {
                                ...responsiveUnit,
                                ...personalizeDiscoveryUnitPropData(
                                    gettext('Edit occupation'),
                                    `${PERSONALIZE_URL}/occupation`,
                                    new UpdateCareerGoalSelected(
                                        GoalCollectionTargetPage.OCCUPATION,
                                        UI_REGION.COURSE_RECOMMENDATION_UNIT,
                                    ),
                                    true,
                                    true,
                                ),
                                carouselProps: {
                                    ariaLabel: unit.title,
                                    ariaRoleDescription: 'carousel',
                                },
                            },
                        };
                    default:
                        return {
                            component: CourseUnit,
                            componentProps: {
                                ...responsiveUnit,
                                carouselProps: {
                                    ariaLabel: unit.title,
                                    ariaRoleDescription: 'carousel',
                                },
                            },
                            contextObject: {
                                context2: 'featured',
                                subcontext: unit.title,
                                subcontext2: unit.id,
                            },
                        };
                }
        }
    };

    const getBundles: () => Promise<void> = async () => {
        const {topicInterests} = await useTopicInterestsQuery.fetcher({})();
        const topicUrls = topicInterests.map((interest) => {
            const parts = interest.url.split('/');
            return parts[parts.length - 1];
        });
        const bundles = await Promise.all(
            topicUrls.map(async (topicUrl) => {
                const bundle = getBundleFromTopic(topicUrl);
                if (!bundle) return null;
                const courseIds = bundle.contents.map((content) => content.id);
                const coursesData = await getCoursesData(courseIds);
                const validCourses = coursesData.filter(
                    (course): course is BrowseCourse => !!course && 'id' in course && !!course.id,
                );
                bundle.contents = validCourses;
                return bundle;
            }),
        );
        const validBundles = bundles.filter((bundle) => bundle !== null);
        setCertBundles(validBundles);
    };

    const renderCertificationBundleUnit = () => {
        const showCertificationExperiment =
            certificationBundleUnitLIHPExperiment?.configuration?.show ?? false;

        triggerImpressionEvent(certificationBundleUnitLIHPExperiment);
        if (!certBundles.length || !showCertificationExperiment) return null;

        const validBundles = certBundles.filter((bundle) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return bundle.contents.every((course: any) => !course?.is_user_subscribed);
        });

        if (validBundles.length === 0) return null;

        const bundleWithMostSubscribers = validBundles.reduce((prev, current) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const prevSubscribers = prev.contents.reduce((sum, course: any) => {
                if (!course.is_user_subscribed) {
                    return sum + course.num_subscribers;
                }
                return sum;
            }, 0);

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const currentSubscribers = current.contents.reduce((sum, course: any) => {
                if (!course.is_user_subscribed) {
                    return sum + course.num_subscribers;
                }
                return sum;
            }, 0);

            return currentSubscribers > prevSubscribers ? current : prev;
        }, validBundles[0]);

        return (
            <div className={styles['topic_certification-bundle-unit-wrapper']}>
                <CertificationBundleUnit
                    isBundleDiscountEnabled={true}
                    bundleDiscountCode={CERT_BUNDLE_DISCOUNT_CODE}
                    certificationBundle={bundleWithMostSubscribers}
                />
            </div>
        );
    };

    const [voucherRecoBundle, setVoucherRecoBundle] = React.useState<CertificationBundle | null>(
        props.bundle ?? null,
    );

    useEffect(() => {
        const hydrateVoucherBundle = async () => {
            if (!props.bundle) {
                setVoucherRecoBundle(null);
                return;
            }

            const courseIds = props.bundle.contents.map((content) => content.id);
            if (!courseIds.length) {
                setVoucherRecoBundle(props.bundle);
                return;
            }

            const coursesData = await getCoursesData(courseIds, ['objectives_summary']);

            const validCourses = (coursesData ?? []).filter(
                (course): course is BrowseCourse => !!course && 'id' in course && !!course.id,
            );

            setVoucherRecoBundle({
                ...props.bundle,
                contents: validCourses,
            });
        };

        void hydrateVoucherBundle();
    }, [props.bundle]);

    const renderCertificationVoucherRecoBanner = () => {
        const {topicSlug, certification, recommendedExam} = props;
        const bundle = voucherRecoBundle;
        const showCertificationVoucherRecoBannerExperiment =
            lihpCertVoucherRecoBannerExperiment?.configuration?.showBanner ?? false;

        triggerImpressionEvent(lihpCertVoucherRecoBannerExperiment);
        if (!showCertificationVoucherRecoBannerExperiment) return null;

        if (
            !topicSlug ||
            !bundle ||
            !bundle.contents?.length ||
            !certification ||
            !recommendedExam
        ) {
            return null;
        }

        return (
            <div className={classNames('component-margin')}>
                <FunnelLogContextProvider pageType={PAGE_TYPE_LOGGED_IN_HOMEPAGE}>
                    <TrackingContextProvider
                        trackingContext={{
                            trackImpressionFunc: discoveryTracker.trackDiscoveryImpression,
                            backendSource: BackendSourceOptions.DISCOVERY,
                        }}
                    >
                        <CertificationPrepRecoBanner
                            topicSlug={topicSlug}
                            certificationBundleCourses={bundle.contents as BrowseCourse[]}
                            certification={certification as BadgeClass}
                            recommendedExam={recommendedExam as RecommendedCertificationVoucherExam}
                        />
                    </TrackingContextProvider>
                </FunnelLogContextProvider>
            </div>
        );
    };

    const discoveryContainerFilter = (u: Partial<DiscoveryUnit<DiscoveryUnitItem>>) =>
        u.view_type !== TOP_10_COURSES_IN_COUNTRY_VIEW_TYPE;

    const renderDiscoveryUnits = () => {
        const itemIndices = localeSpecificTopicsFeature.show_locale_specific_topic
            ? {bestseller_labels: 0}
            : {};
        return (
            <>
                <Provider
                    showPersonalPlanBadge={props.contextData?.showPersonalPlanBadge}
                    showCodingExercisesBadge={props.contextData?.showCodingExercisesBadge}
                >
                    <DiscoveryUnitsContainer
                        pageType={PAGE_TYPE_LOGGED_IN_HOMEPAGE}
                        getUnitComponentAndProps={getUnitComponentAndProps}
                        filter={discoveryContainerFilter}
                        itemIndicesByType={itemIndices}
                        fetchOptions={{
                            pageSize: 3,
                        }}
                    />
                </Provider>
            </>
        );
    };

    const renderMyLearningUnit = () => {
        return (
            <div className="my-learning-wrapper component-margin">
                <FunnelLogContextProvider pageType={FUNNEL_LOG_PAGE_TYPE}>
                    <MyLearningUnitTabProvider learningTabs={[learningTab.current]}>
                        <MyLearningUnitContainer
                            title={gettext("Let's start learning")}
                            url={MY_COURSES_HOME_URL}
                            learningTabs={[learningTab.current]}
                            preloadedData={props.userEnrollments}
                        />
                    </MyLearningUnitTabProvider>
                </FunnelLogContextProvider>
            </div>
        );
    };

    const renderPopularCategoriesModule = () => {
        return (
            <div className="component-margin">
                <TopCategoriesComponent />
            </div>
        );
    };

    const renderTop10CoursesModule = () => {
        const enableTop10CoursesJapan =
            top10CoursesJapanExperiment?.configuration?.enableTop10CoursesJapan ?? false;

        if (!enableTop10CoursesJapan) return null;

        triggerImpressionEvent(top10CoursesJapanExperiment);

        return (
            <div className={classNames('component-margin', styles.bleed)}>
                <Top10CoursesComponent />
            </div>
        );
    };

    const renderStreaksUnit = () => {
        if (streaksStore.errorState) {
            return null;
        }
        return (
            <div className="component-margin">
                {streaksStore.isLoading ? (
                    <TextSkeleton withTitle={true} lineCountPerParagraph={2} />
                ) : streaksStore.streakData ? (
                    <StreaksBox streakData={streaksStore.streakData} />
                ) : null}
            </div>
        );
    };

    useEffect(() => {
        const {showStreaksOnMXLIHP: showUserStreaks} =
            userStreakOnMXLIHPExperiment?.configuration ?? {};
        setShowUserStreaks(showUserStreaks);
        if (showUserStreaks) {
            streaksStore.load();
        }
    }, [streaksStore, userStreakOnMXLIHPExperiment]);

    useEffect(() => {
        const showCertificationExperiment =
            certificationBundleUnitLIHPExperiment?.configuration?.show ?? false;
        showCertificationExperiment ? getBundles() : null;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [certificationBundleUnitLIHPExperiment]);

    return (
        <OnePaneLayoutContainer>
            <OnePaneLayoutFullWidthSection>
                <UserOccupationGoalHeader
                    userOccupationInfo={props.userOccupationInfo}
                    hasSeenHeader={props.hasSeenUserGoalHeader}
                    showBackground={false}
                />
                <GwgSection position="top" accelerator={props.accelerator} />
            </OnePaneLayoutFullWidthSection>
            <div className={classNames('ud-page-wrapper', styles['page-wrapper-no-top-padding'])}>
                {renderCertificationBundleUnit()}

                {showCourseGuidanceFeature.show_course_guidance_feature && (
                    <div className="component-margin">
                        <CourseGuidanceEntryPointWithPreselection />
                    </div>
                )}
                <EntryPointPopUp />

                {renderTop10CoursesModule()}
                {isPriceCountryJapan && renderPopularCategoriesModule()}

                {renderMyLearningUnit()}

                {showUserStreaks && learningTab.current.hasContent && renderStreaksUnit()}

                {renderCertificationVoucherRecoBanner()}
                <div id={COURSE_RECOMMENDATIONS_SECTION_ID} className="component-margin">
                    <h2 className={classNames('ud-heading-serif-xxl', styles['section-title'])}>
                        {gettext('What to learn next')}
                    </h2>
                    {renderDiscoveryUnits()}
                    <GwgSection position="bottom" accelerator={props.accelerator} />
                </div>
            </div>
        </OnePaneLayoutContainer>
    );
});
